/* General utility functions (exposes cn, hexToHsl, formatCpfCnpj, formatPhone, applyTheme) */
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { SiteSettings } from '@/services/siteSettingsService'

/**
 * Merges multiple class names into a single string
 * @param inputs - Array of class names
 * @returns Merged class names
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Converts a Hex color string to HSL values (e.g. "118 36% 31%")
 * @param hex - Hex color string (e.g. #346a32)
 * @returns HSL string
 */
export function hexToHsl(hex: string): string | null {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  hex = hex.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b
  })

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return null

  let r = parseInt(result[1], 16)
  let g = parseInt(result[2], 16)
  let b = parseInt(result[3], 16)

  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  let l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }

  // Round values
  h = Math.round(h * 360)
  s = Math.round(s * 100)
  l = Math.round(l * 100)

  return `${h} ${s}% ${l}%`
}

/**
 * Applies site settings to CSS variables
 * @param settings - Site settings object
 */
export function applyTheme(settings: SiteSettings) {
  const root = document.documentElement

  if (settings.primary_color) {
    const hsl = hexToHsl(settings.primary_color)
    if (hsl) {
      root.style.setProperty('--primary', hsl)
      // Also update ring to match primary
      root.style.setProperty('--ring', hsl)
    }
  }

  if (settings.accent_color) {
    const hsl = hexToHsl(settings.accent_color)
    if (hsl) {
      root.style.setProperty('--accent', hsl)
    }
  }
}

/**
 * Formats a string as CPF or CNPJ
 * @param value - The input string
 * @returns Formatted CPF or CNPJ
 */
export function formatCpfCnpj(value: string) {
  const digits = value.replace(/\D/g, '')

  if (digits.length <= 11) {
    // CPF
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1')
  } else {
    // CNPJ
    return digits
      .slice(0, 14)
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
  }
}

/**
 * Formats a string as a Brazilian phone number
 * @param value - The input string
 * @returns Formatted phone number
 */
export function formatPhone(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  if (digits.length <= 2) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  if (digits.length <= 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
}
